export const tableSelectionKey = (entity) => `${entity.type || "table"}:${entity.id}`;

export const tableName = (entity) => String(entity.name ?? "").trim() || String(entity.id);

export function tableCapacity(entity) {
    const capacities = (entity.enabledPax ?? []).map(Number).filter((capacity) => capacity > 0);
    return capacities.length ? Math.max(...capacities) : Number(entity.seats) || 0;
}

export const selectedTableCapacity = (entities) => entities.reduce((total, entity) => total + tableCapacity(entity), 0);

const membersOf = (entity) => (entity.type === "group" ? (entity.tables ?? []) : [entity]);
const sameId = (left, right) => String(left) === String(right);

function isAvailable(table, { slotId, startsAt, endsAt, baseTableIds }) {
    if (baseTableIds.some((id) => sameId(id, table.id))) return true;
    const slot = table.slots?.find((slot) => sameId(slot.id, slotId));
    if (!slot?.entityEnabled || !startsAt || !endsAt) return false;
    return !table.slots.some((other) => {
        if (sameId(other.id, slotId)) return false;
        const [hour, minute] = other.hour_start.split(":").map(Number);
        const time = startsAt.set({ hour, minute });
        return startsAt <= time && time < endsAt && other.entityEnabled === false;
    });
}

// Project the server's seating plan into choices; booking eligibility stays on the API.
export function buildTableRooms(plan, { slotId, startsAt, endsAt, selected = [], guests, baseTableIds = [] }) {
    const selectedKeys = new Set(selected.map(tableSelectionKey));
    const selectedIds = new Set(selected.flatMap(membersOf).map((table) => String(table.id)));
    const roomId = selected[0]?.roomId;
    const enoughSeats = selectedTableCapacity(selected) >= guests;

    return (plan?.rooms ?? [])
        .filter((room) => room.isEnabledForService || Number(room.enabled) === 1)
        .map((room) => ({
            id: room.id,
            roomName: room.name,
            entities: room.entities
                .filter(
                    (entity) => entity.enabled && ["table", "group"].includes(entity.type) && entity.slots?.some((slot) => sameId(slot.id, slotId))
                )
                .map((entity) => {
                    const selectionKey = tableSelectionKey(entity);
                    const isSelected = selectedKeys.has(selectionKey);
                    const members = membersOf(entity);
                    let disabledReason = null;
                    if (!isSelected) {
                        if (!members.length || !members.every((table) => isAvailable(table, { slotId, startsAt, endsAt, baseTableIds }))) {
                            disabledReason = "unavailable";
                        } else if (roomId !== undefined && !sameId(roomId, room.id)) {
                            disabledReason = "differentRoom";
                        } else if (members.some((table) => selectedIds.has(String(table.id)))) {
                            disabledReason = "overlap";
                        } else if (enoughSeats) {
                            disabledReason = "enoughSeats";
                        }
                    }
                    return { ...entity, selectionKey, roomId: room.id, roomName: room.name, disabledReason, $isDisabled: Boolean(disabledReason) };
                }),
        }))
        .filter((room) => room.entities.length);
}

export function filterTableRooms(rooms, query, roomId = null) {
    const normalize = (value) =>
        String(value)
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLocaleLowerCase();
    const term = normalize(query).trim().replace(/\s+/g, " ");
    return rooms
        .filter((room) => roomId === null || sameId(room.id, roomId))
        .map((room) => ({
            ...room,
            entities: room.entities.filter((entity) => {
                const label = normalize(
                    [
                        room.roomName,
                        entity.type === "group" ? "groupe group" : "table",
                        tableName(entity),
                        ...membersOf(entity).map((table) => `table ${tableName(table)}`),
                    ].join(" ")
                );
                return label.includes(term);
            }),
        }))
        .filter((room) => room.entities.length);
}
